/**
 * Stair calculator (Phase 3 — docs/bim-plan/03 Phase 3): Revit-style. Given the
 * floor-to-floor rise and a MAX riser, the count is the smallest riser count that
 * keeps every riser at or under the max (ceil), and the actual riser height divides
 * the rise exactly — 10'-0" at 7.5" max → 16 risers @ 7.5" even (the acceptance
 * case, unit-tested). The top riser lands ON the upper floor, so treads = risers − 1.
 */
export interface StairRun {
  risers: number
  riserFt: number
  treads: number
  runFt: number
}

export function stairRun(floorToFloorFt: number, maxRiserFt: number, treadDepthFt: number): StairRun {
  const rise = Math.max(floorToFloorFt, 1e-6)
  const maxR = Math.max(maxRiserFt, 1e-6)
  const risers = Math.max(1, Math.ceil(rise / maxR - 1e-9))
  const riserFt = rise / risers
  const treads = Math.max(0, risers - 1)
  return { risers, riserFt, treads, runFt: treads * treadDepthFt }
}

/**
 * STAIR INSTANCE STEPS (2026-09-30, Matt: "the stairs are fixed at risers and treads
 * based on type … I'd like those to be instance properties"): Revit's split — the TYPE
 * keeps the firm's limits (Max Riser, the standard Tread Depth); each stair INSTANCE may
 * set its own Number of Risers (`desiredRisers`) and Tread Depth (`actualTreadDepthFt`).
 * Blank = the type-driven default (fewest risers under the max; the type's tread). The
 * ONE place those resolve — every consumer (3D, plan annotation, grips, pick, the run-
 * length normalizer) reads its stairRun inputs from here so they can never disagree.
 * A set riser count is expressed as the max riser that yields exactly that count
 * (rise / n — stairRun's ceil lands on n), so stairRun itself is unchanged.
 */
export function stairStepInputs(
  params: Record<string, unknown>,
  riseFt: number | null | undefined,
): { maxRiserFt: number; treadDepthFt: number } {
  const typeMax = (params.maxRiserFt as number) || 7.5 / 12
  const n = Math.round(Number(params.desiredRisers))
  const rise = typeof riseFt === 'number' && riseFt > 0 ? riseFt : null
  const maxRiserFt = n >= 1 && rise ? rise / n : typeMax
  const inst = Number(params.actualTreadDepthFt)
  const treadDepthFt = inst > 0 ? inst : ((params.treadDepthFt as number) || 11 / 12)
  return { maxRiserFt, treadDepthFt }
}

export interface StairVerticalExtent {
  zBase: number
  zTop: number
}

/**
 * BOTTOM/TOP CONSTRAINTS round (2026-08-23): the ONE place a stair's `baseMode`/
 * `topMode` constraints resolve into absolute project-relative z values — every
 * geometry/UI consumer calls this instead of re-deriving `zBase`/`zTop` inline (see
 * the schema header comment in `categories/stair/schema.ts` for the mode vocabulary).
 * `'level'` modes read the already-materialized `paramLevels` (resolveElement's
 * paramLevels pattern, same as column's `topLevelId`) and fall back to the
 * `'offset'`/`'rise'` branch when the referenced level is unset/dangling — a
 * half-configured stair never vanishes. `'site'` modes read a plain project-relative
 * length param (real-world conversion happens only at the UI boundary, never here).
 */
export function stairVerticalExtent(el: {
  level: { elevationFt: number } | null
  params: Record<string, unknown>
  paramLevels?: Record<string, { elevationFt: number }>
}): StairVerticalExtent | null {
  if (!el.level) return null
  const num = (k: string, fallback: number) => (typeof el.params[k] === 'number' ? (el.params[k] as number) : fallback)

  const baseMode = el.params.baseMode as string | undefined
  const baseLevel = baseMode === 'level' ? el.paramLevels?.baseLevelId : undefined
  const zBase = baseLevel
    ? baseLevel.elevationFt + num('baseLevelOffsetFt', 0)
    : baseMode === 'site'
      ? num('baseSiteElevationFt', 0)
      : el.level.elevationFt + num('baseOffsetFt', 0)

  const topMode = el.params.topMode as string | undefined
  const topLevel = topMode === 'level' ? el.paramLevels?.topLevelId : undefined
  const zTop = topLevel
    ? topLevel.elevationFt + num('topLevelOffsetFt', 0)
    : topMode === 'site'
      ? num('topSiteElevationFt', 0)
      : zBase + num('floorToFloorFt', 10)

  return { zBase, zTop }
}
